import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BalanceRouteMetricCard } from '../../../../components/RouteCards/BalanceRouteMetricCard';
import { routeToBalanceRouteCardProps } from '@components/RouteCards/utils';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { LinearGauge } from '../../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { useRouteById } from '../../../../hooks/useRouteById';
import { IBalanceFlowProps, IBalanceInstruction } from '../../../../sp-types/balanceFlowType';
import { createEmptyRiskTolerance, getRiskTolerance } from '../../../../utils/accountUtils';
import {
  getBalanceArrowTypeLocation,
  getExecutionInstructions,
  updateBalanceStepsStatusByIndexes,
} from '../../balanceUtils';
import { getRoutePoliciesByMaterial } from '../../../../sp-firebase/routePolicyFB';
import { sortByOrder } from '../../../Policy/MaterialDetails/components/MaterialDetailUtils';
import { IRoute, IRoutePolicy } from '../../../../sp-types';
import { getRouteTotalPrice, mergeRoutesAndPolicies } from '../../../../utils/routesUtils';
import { RouteDetailsPopup } from '../../../Policy/MaterialDetails/components/RouteDetailPopup';
import { FormatToShortPriceTemplate, formatToShortString } from '../../../../utils/FormatValue';
import { Link, useParams } from 'react-router-dom';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';

const sortByExpiryDate = (a: IBalanceInstruction, b: IBalanceInstruction): number =>
  a.expiryDate.toDate().getTime() - b.expiryDate.toDate().getTime();

export const BalanceFlowPreferredRoute: React.FC<IBalanceFlowProps> = ({
  balanceFlowData,
  updateBalanceFlowData,
  currentStepIndex,
  setCurrentStepIndex,
  setIsLoading,
  material,
  routesData,
  routePolicies,
}) => {
  const { balanceID } = useParams<{ balanceID: string }>();

  const { materialsMap } = useMaterialsStore();
  const { formulaId, exposureBeforeBalance, accountId } = balanceFlowData;
  const { account } = useAccountStore();
  const [showPopup, setShowPopup] = React.useState<IRoute | null>(null);

  const [balanceArrowData, setBalanceArrowData] = useState<{ value: number; img: string } | undefined>();

  useEffect(() => {
    setBalanceArrowData(getBalanceArrowTypeLocation(account, formulaId, balanceFlowData));
  }, [balanceFlowData, exposureBeforeBalance, formulaId]);

  React.useEffect(() => {
    if (routePolicies && routesData) {
      if (location.pathname.indexOf(balanceFlowData.id) > 0) {
        updateBalanceByUpdatedData();
      } else {
        console.debug('balance are not same', balanceFlowData.id);
      }
    }
  }, [routesData, routePolicies, balanceID]);

  async function updateBalanceByUpdatedData() {
    const currBalanceRoutes = balanceFlowData.orderedRoutes || routesData;
    if (!currBalanceRoutes || currBalanceRoutes.length === 0) {
      return;
    }

    setIsLoading(true);

    const balanceInstructions: IBalanceInstruction[] = balanceFlowData.balanceInstructions?.length
      ? balanceFlowData.balanceInstructions
      : await getExecutionInstructions(balanceFlowData.materialExposure, currBalanceRoutes);

    const noInstructions = balanceInstructions.length === 0;
    const orderedRoutes =
      balanceFlowData.orderedRoutes ||
      mergeRoutesAndPolicies(routesData || [], routePolicies || [], balanceInstructions);

    const stepsUpdates = noInstructions
      ? [{ index: currentStepIndex, status: 'Pending' as const }]
      : [
          { index: currentStepIndex - 1, status: 'Approved' as const },
          { index: currentStepIndex, status: 'Incomplete' as const },
        ];

    const steps = updateBalanceStepsStatusByIndexes(balanceFlowData.steps, stepsUpdates);

    await updateBalanceFlowData({
      ...balanceFlowData,
      isBalanceBlocked: noInstructions,
      routeIds: routesData?.map((item) => item.id),
      steps,
      balanceInstructions: balanceInstructions.sort(sortByExpiryDate),
      orderedRoutes,
    });

    setIsLoading(false);
    if (noInstructions) {
      setCurrentStepIndex(1);
    }
  }

  const goNextStepIfPossible = () => {
    if (balanceFlowData.balanceInstructions?.length !== 0) {
      setCurrentStepIndex((prevIndex: number) => prevIndex + 1);
    }
  };

  const toDisplayPrice = (price: number): string => (price === 0 ? '0' : formatToShortString(price, 0));

  const getTotalQuantity = (routes: IRoute[]): string =>
    toDisplayPrice(routes.reduce((res, { instructionsQuantity }) => res + instructionsQuantity, 0));

  const getTotalPrice = (routes: IRoute[]): string =>
    FormatToShortPriceTemplate(routes.reduce((res, { tranPrice }) => res + tranPrice, 0));

  return routesData && balanceFlowData.orderedRoutes ? (
    <div className="flex flex-1 flex-col  ">
      <div className="mb-[1rem]">
        <h2 className="text-[1.125rem] leading-4 lg:block xl:block sm:hidden md:hidden ">Preferred routes</h2>
        <p className="text-secondary m-0   ">Below are the optimal hedging routes based on your profile</p>
      </div>
      <div className="border-t border-t-gray-100 pt-[1.5rem] space-y-[1.5rem] ">
        <h6 className="text-red m-0">Expected balance</h6>

        <div className=" flex flex-col  flex-1 grow mx-auto  w-[70%] space-y-[3rem]">
          <LinearGauge
            isZoomExposure
            hightInRem={1.5}
            currentValue={exposureBeforeBalance}
            riskTolerance={getRiskTolerance(formulaId, account) || createEmptyRiskTolerance()}
            numberOfTicks={12}
            {...(balanceArrowData !== undefined
              ? { additionalArrows: [{ img: balanceArrowData.img, value: balanceArrowData.value }] }
              : {})}
          />
          <div className="flex flex-col space-y-[0.75rem]  grow justify-center "></div>
        </div>
        <div className="flex flex-col gap-3">
          {routesData &&
            balanceFlowData.orderedRoutes?.map((route, index) => (
              <div className="flex gap-2 justify-between flex-1" key={index} onDoubleClick={() => setShowPopup(route)}>
                <BalanceRouteMetricCard
                  {...routeToBalanceRouteCardProps(
                    route,
                    () => {},
                    route.formulaId,
                    index + 1 + '.',
                    materialsMap[route.formulaId],
                  )}
                />
                <div
                  className="flex flex-col border-2 text-sm justify-center p-2 gap-2 w-24 border-gray-125"
                  title={toDisplayPrice(route.instructionsQuantity || 0)}
                >
                  <p className="text-gray-300">Quantity</p>
                  <p className="text-base align-center text-ellipsis">
                    {toDisplayPrice(route.instructionsQuantity || 0)}
                  </p>
                </div>
              </div>
            ))}
          <div className="flex gap-2 w-full mb-3 ">
            <div className="flex flex-1 border-2 justify-between px-4 py-2 gap-2 w-22 border-gray-125 w-full">
              <div className="font-bold p-2">Total:</div>
              <div className="flex flex-col gap-0 justify-start align-top">
                <div className="text-sm">Expected price</div>
                <div className="text-blue-300">{getTotalPrice(balanceFlowData?.orderedRoutes || [])}</div>
              </div>
            </div>
            <div className="flex flex-0 flex-col p-2 px-1 gap-0 w-24 border-2 border-gray-125 ">
              <div className="text-sm">Total</div>
              <div className="text-red-300">{getTotalQuantity(balanceFlowData?.orderedRoutes || [])}</div>
            </div>
          </div>
          {showPopup && (
            <RouteDetailsPopup onClose={() => setShowPopup(null)} route={showPopup} isDarkShade isFullScreen />
          )}
        </div>
        <div className="mt-auto  space-x-4 flex justify-between">
          <div className="space-x-4 flex sticky bottom-0 bg-white p-2 ">
            <div className="flex">
              <ButtonDesigned
                backButton
                className="py-2 "
                style="outline"
                onButtonClick={() => setCurrentStepIndex((currIndex) => currIndex - 1)}
              ></ButtonDesigned>
            </div>
            <ButtonDesigned disableButton={!routesData} onButtonClick={() => goNextStepIfPossible()}>
              Next <ChevronRightIcon />
            </ButtonDesigned>
          </div>
          <Link to={'./edit-routes'}>
            <ButtonDesigned style="outline" className=" " onButtonClick={() => 1}>
              <CreateOutlinedIcon className="text-sm me-2" />
              Edit
            </ButtonDesigned>
          </Link>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex flex-col flex-1">
      <div className="h-24 bg-gray-100 animate-pulse rounded-xl">Loading...</div>
    </div>
  );
};
